import {create, StateCreator} from 'zustand';
import {devtools} from 'zustand/middleware';

import {Experiment, ExperimentMetric, ExperimentMetricResults, ExperimentMetricType} from 'apps/experiments/src/types';
import {DeepPartial} from 'common/util/typescript';
import {InsightsQueryResults} from 'common/report/insights/util/types';
import {InsightsBookmarkParams} from 'common/types/reports/bookmark';
import {createStateWithHistorySlice} from 'common/zustand/state-with-history';

export type MetricGroups = Record<ExperimentMetricType, ExperimentMetric[]>;
export type MetricResult = Record<string, ExperimentMetricResults> | Error;
export type MetricResults = Record<string, MetricResult>;

const createExperimentSlice = createStateWithHistorySlice<Experiment, `experiment`>(`experiment`);

type AppState = {
  experiments?: Experiment[];
  draftExperiment?: DeepPartial<Experiment>;
  experimentLoading: boolean;
  currentMetric?: ExperimentMetric;
  metricResults: MetricResults;
  exposuresBookmarkParams?: InsightsBookmarkParams;
  exposuresQueryResults?: InsightsQueryResults;
};

type AppStore = AppState & {
  setExperiments: (experiments: Experiment[]) => void;
  setDraftExperiment: (experiment?: DeepPartial<Experiment>) => void;
  setExperimentLoading: (isLoading: boolean) => void;
  setCurrentMetric: (metric?: ExperimentMetric) => void;
  setMetricResults: (results: MetricResults) => void;
  setExposuresBookmarkParams: (params?: InsightsBookmarkParams) => void;
  setExposuresQueryResults: (results?: InsightsQueryResults) => void;
  reset: () => void;
};

export type ExperimentAppStore = AppStore & ReturnType<typeof createExperimentSlice>;

const initialState: AppState = {
  experiments: undefined,
  draftExperiment: undefined,
  experimentLoading: false,
  currentMetric: undefined,
  metricResults: {},
  exposuresBookmarkParams: undefined,
  exposuresQueryResults: undefined,
};

export const EMPTY_METRIC_ID = `empty`;

const createAppSlice: StateCreator<ExperimentAppStore, [], [], AppStore> = (set) => ({
  ...initialState,
  setExperiments: (experiments) => set({experiments}),
  setDraftExperiment: (draftExperiment) => set({draftExperiment}),
  setExperimentLoading: (experimentLoading) => set({experimentLoading}),
  setCurrentMetric: (currentMetric) => set({currentMetric}),
  setMetricResults: (metricResults) => set({metricResults}),
  setExposuresBookmarkParams: (exposuresBookmarkParams) => set({exposuresBookmarkParams}),
  setExposuresQueryResults: (exposuresQueryResults) => set({exposuresQueryResults}),
  reset: () => set({...initialState}),
});

export const useExperimentsStore = create<ExperimentAppStore>()(
  devtools((...a) => ({
    ...createExperimentSlice(...a),
    ...createAppSlice(...a),
  })),
);
